import { IDDateRange } from "src/app/components/InsightsDashboard/types";
import {
  CLEAR_STATE,
  GET_DASHBOARD_LIST_FAIL,
  GET_DASHBOARD_LIST_SUCCESS,
  GET_SETUP_CONFIG_FAIL,
  GET_SETUP_CONFIG_SUCCESS,
  SET_ID_DATES,
  SET_INITIAL_SETUP_DONE,
  SET_ID_PRINTING,
  SET_SELECTED_DASHBOARD,
  SET_SETUP,
  SET_SETUP_CONFIG,
  SET_SETUP_KEY,
  SET_SHOW_PUBLICATIONS_FILTER,
  SET_SHOW_TRENDS,
} from "./insightsDashboardTypes";
import {
  InsightsDashboardSetup,
  InsightsDashboardSetupConfig,
  WorkspacePublicationsType,
} from "./types";
import { SocialInsightsDashboardListElement, initialFilters } from "./utils";
import { IDDateRangeOption } from "src/app/components/InsightsDashboard/InsightsDashboard";
import { getInsightsDashboardDefaultDates } from "src/app/components/InsightsDashboard/utils";

export interface InsightsDashboardReducerState {
  setupConfig: InsightsDashboardSetupConfig | null;
  setupConfigLoading: boolean;
  dateRanges: IDDateRange[];
  setup: InsightsDashboardSetup;
  initialSetupDone: boolean;
  showTrends: boolean;
  showPublicationsFilter: boolean;
  dashboardList: SocialInsightsDashboardListElement[];
  dashboardListFirstLoadDone: boolean;
  selectedDashboard: SocialInsightsDashboardListElement | null;
  dateFrom: string | null;
  dateTo: string | null;
  dateRange: IDDateRangeOption | null;
  printing: boolean;
  accessDenied: boolean;
}

const defaultDates = getInsightsDashboardDefaultDates();

const initialState: InsightsDashboardReducerState = {
  setupConfig: null,
  setupConfigLoading: true,
  dateRanges: [],
  setup: {
    ...initialFilters,
    workspacePublicationsType: WorkspacePublicationsType.WorkspaceOnly,
    hashtagsCriteria: { value: "or", label: "Or" },
    mentionsCriteria: { value: "or", label: "Or" },
    hashtagsMentionsCriteria: { value: "or", label: "Or" },
  },
  initialSetupDone: false,
  showTrends: false,
  showPublicationsFilter: false,
  dashboardList: [],
  dashboardListFirstLoadDone: false,
  selectedDashboard: null,
  dateFrom: defaultDates.dateFrom,
  dateTo: defaultDates.dateTo,
  dateRange: defaultDates.dateRange,
  printing: false,
  accessDenied: false,
};

// eslint-disable-next-line @typescript-eslint/default-param-last
function insightsDashboardReducer(state = initialState, action: any) {
  switch (action.type) {
    case SET_SETUP:
      return {
        ...state,
        setup: action.payload.setup,
      };

    case SET_SETUP_KEY:
      return {
        ...state,
        setup: {
          ...state.setup,
          [action.payload.key]: action.payload.value,
        },
      };

    case SET_INITIAL_SETUP_DONE:
      return {
        ...state,
        initialSetupDone: true,
      };

    case SET_SHOW_TRENDS:
      return {
        ...state,
        showTrends: action.payload.showTrends,
      };

    case SET_SETUP_CONFIG:
      return {
        ...state,
        setupConfig: action.payload.setupConfig,
      };

    case SET_SHOW_PUBLICATIONS_FILTER:
      return {
        ...state,
        showPublicationsFilter: action.payload.showPublicationsFilter,
      };

    case GET_DASHBOARD_LIST_SUCCESS:
      return {
        ...state,
        dashboardList: action?.payload?.data?.content || [],
        dashboardListFirstLoadDone: true,
      };

    case SET_SELECTED_DASHBOARD:
      if (!action.payload.dashboard) {
        return {
          ...state,
          selectedDashboard: null,
          setup: initialState.setup,
          showTrends: initialState.showTrends,
          dateFrom: defaultDates.dateFrom,
          dateTo: defaultDates.dateTo,
          dateRange: defaultDates.dateRange,
        };
      }

      return {
        ...state,
        selectedDashboard: action.payload.dashboard,
        dashboardList: state.dashboardList.map((dashboard) => {
          if (dashboard.uuid === action.payload.dashboard.uuid) {
            return action.payload.dashboard;
          }
          return dashboard;
        }),
      };

    case GET_SETUP_CONFIG_SUCCESS: {
      const setupConfigContent = action?.payload?.data?.content || {};
      const { dateRanges, showPublicationsFilter, ...contentRest } =
        setupConfigContent;

      return {
        ...state,
        showPublicationsFilter,
        dateRanges,
        setupConfig: contentRest,
        setupConfigLoading: false,
      };
    }

    case SET_ID_DATES:
      return {
        ...state,
        ...action.payload,
      };

    case SET_ID_PRINTING:
      return {
        ...state,
        printing: action.payload.printing,
      };

    case GET_SETUP_CONFIG_FAIL:
    case GET_DASHBOARD_LIST_FAIL: {
      const status = action.payload.error.response.status;

      if (status === 401 || status === 403) {
        return {
          ...state,
          accessDenied: true,
        };
      }

      return { ...state, setupConfigLoading: false };
    }

    case CLEAR_STATE:
      return initialState;

    default:
      return state;
  }
}

export default insightsDashboardReducer;
